// IPv4 subnet math — mirrors the backend (Python ipaddress) and the proven POC.

export function isValidIPv4(ip) {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec((ip || '').trim());
  if (!m) return false;
  return m.slice(1).every((o) => {
    const n = Number(o);
    return n >= 0 && n <= 255 && String(n) === String(Number(o));
  });
}

export function ipToInt(ip) {
  return (
    ip
      .trim()
      .split('.')
      .reduce((acc, o) => ((acc << 8) >>> 0) + Number(o), 0) >>> 0
  );
}

export function intToIp(n) {
  n = n >>> 0;
  return [24, 16, 8, 0].map((s) => (n >>> s) & 255).join('.');
}

export function parseCidr(cidr) {
  const parts = (cidr || '').trim().split('/');
  if (parts.length !== 2) return null;
  const ip = parts[0];
  const prefix = parseInt(parts[1], 10);
  if (!isValidIPv4(ip) || Number.isNaN(prefix) || prefix < 0 || prefix > 32) return null;
  return { ip, prefix };
}

export function subnetInfo(cidr) {
  const p = parseCidr(cidr);
  if (!p) return { valid: false, error: 'Invalid CIDR' };
  const { ip, prefix } = p;
  const ipInt = ipToInt(ip);
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const network = (ipInt & mask) >>> 0;
  const broadcast = (network | (~mask >>> 0)) >>> 0;
  const total = Math.pow(2, 32 - prefix);
  let usable, firstHost, lastHost;
  if (prefix <= 30) {
    usable = total - 2;
    firstHost = intToIp(network + 1);
    lastHost = intToIp(broadcast - 1);
  } else if (prefix === 31) {
    usable = 2;
    firstHost = intToIp(network);
    lastHost = intToIp(broadcast);
  } else {
    usable = 1;
    firstHost = intToIp(network);
    lastHost = intToIp(network);
  }
  return {
    valid: true,
    input: cidr,
    network: intToIp(network),
    broadcast: intToIp(broadcast),
    netmask: intToIp(mask),
    prefix,
    total,
    usable: usable < 0 ? 0 : usable,
    firstHost,
    lastHost,
  };
}

export function ipInSubnet(ip, cidr) {
  const p = parseCidr(cidr);
  if (!p || !isValidIPv4(ip)) return false;
  const mask = p.prefix === 0 ? 0 : (0xffffffff << (32 - p.prefix)) >>> 0;
  return ((ipToInt(ip) & mask) >>> 0) === ((ipToInt(p.ip) & mask) >>> 0);
}

// Returns the "fixed" part of an IP that is fully determined by the CIDR network,
// so an IP input can ask only for the variable host part.
// e.g. "192.168.10.0/24" -> { fixed: "192.168.10.", octets: 3 }
//      "10.0.0.0/8"       -> { fixed: "10.",          octets: 1 }
// Returns null when there is no clean full-octet prefix to offer (prefix < 8 or >= 32).
export function ipFixedPrefix(cidr) {
  const p = parseCidr(cidr);
  if (!p) return null;
  const full = Math.floor(p.prefix / 8);
  if (full < 1 || full > 3) return null;
  const fixed = p.ip.split('.').slice(0, full).join('.') + '.';
  return { fixed, octets: full };
}

// Extract the variable (host) part of a full IPv4 for a given CIDR.
// e.g. /24 "192.168.10.55" -> "55"; /16 "192.168.10.55" -> "10.55".
// Returns the input unchanged if the CIDR has no clean octet prefix or the IP
// does not sit inside that prefix (so nothing is ever silently lost).
export function hostPart(cidr, fullIp) {
  if (!fullIp) return '';
  const p = ipFixedPrefix(cidr);
  if (!p) return fullIp;
  return fullIp.startsWith(p.fixed) ? fullIp.slice(p.fixed.length) : fullIp;
}

// Combine a CIDR's fixed prefix with a variable host part into a full IPv4.
// e.g. /24 + "55" -> "192.168.10.55". If the host already carries the full
// prefix or looks like a complete 4-octet address it is returned unchanged.
export function fullIp(cidr, host) {
  const h = (host || '').trim();
  if (!h) return '';
  const p = ipFixedPrefix(cidr);
  if (!p) return h;
  if (h.startsWith(p.fixed)) return h;
  if (h.split('.').length >= 4) return h;
  return p.fixed + h;
}

export function dhcpValid(cidr, start, end) {
  const info = subnetInfo(cidr);
  if (!info.valid) return { valid: false, error: 'Invalid CIDR' };
  if (!isValidIPv4(start) || !isValidIPv4(end)) return { valid: false, error: 'Invalid IP' };
  const s = ipToInt(start);
  const e = ipToInt(end);
  const net = ipToInt(info.network);
  const bc = ipToInt(info.broadcast);
  const inSubnet = s >= net && s <= bc && e >= net && e <= bc;
  const order = s <= e;
  const notNet = info.prefix <= 30 ? s > net : true;
  const notBc = info.prefix <= 30 ? e < bc : true;
  const valid = inSubnet && order && notNet && notBc;
  return { valid, inSubnet, order, count: valid ? e - s + 1 : 0 };
}
